import React, { useState } from 'react';
import { useVocalConfig } from '@context/VocalConfigContext';
import { useVocalPacks } from '@hooks/useVocalPacks';
import { Button, IconButton } from '../ui/Button';
import { Select, Checkbox } from '../ui/Form';
import { Badge } from '../ui/Badge';
import VocalProfileBrowser from './VocalProfileBrowser';
import { ChevronRight } from 'lucide-react';

interface VocalPackSectionProps {
  onOpenBlender?: () => void;
}

export function VocalPackSection({ onOpenBlender }: VocalPackSectionProps): React.ReactElement {
  const {
    config,
    setSinglePack,
    setVoiceSpec,
    setHarmonyStack,
    setChoirGang,
    clearBlend,
    isBlendActive
  } = useVocalConfig();

  const { data: vocalPacks } = useVocalPacks();
  const [browserOpen, setBrowserOpen] = useState(false);

  const activePack = config.singlePackKey ? vocalPacks[config.singlePackKey] : null;

  const voiceSpecOptions = [
    { value: 'lead', label: 'Primary Lead' },
    ...(activePack?.voiceAltA ? [{ value: 'altA', label: 'Alt Role A' }] : []),
    ...(activePack?.voiceAltB ? [{ value: 'altB', label: 'Alt Role B' }] : [])
  ];

  return (
    <div className="vocal-pack-section">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-slate-400">Vocal Configuration</label>
        {!isBlendActive && (
          <Button
            type="button"
            variant="link"
            size="xs"
            onClick={onOpenBlender}
            className="text-[11px] uppercase tracking-wider"
          >
            🔀 Blender Mode
          </Button>
        )}
      </div>

      {isBlendActive && config.blendOutput ? (
        <div className="bg-slate-900 border border-accent/50 rounded-lg p-4 space-y-3 shadow-[0_0_15px_rgba(245,158,11,0.1)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="primary" size="xs">Active Blend</Badge>
              <h4 className="text-sm font-bold text-slate-200">{config.blendOutput.name}</h4>
            </div>
            <IconButton
              type="button"
              variant="ghost"
              size="xs"
              onClick={clearBlend}
              aria-label="Clear blend"
              className="text-slate-400 hover:text-red-400"
            >
              ✕
            </IconButton>
          </div>

          <p className="text-[11px] text-slate-400 italic leading-relaxed">{config.blendOutput.description}</p>

          <div className="flex flex-wrap gap-2 pt-1">
            <Badge variant="default" size="xs">
              {config.blendConfig?.tempo} BPM
            </Badge>
            <Badge variant="default" size="xs" className="uppercase">
              {config.blendOutput.pacing.label}
            </Badge>
            {/* Short tags only: profile tags lead the list now (AUTO-041) and can run 80+ chars. */}
            {config.blendOutput.sunoStyleTags.filter(t => t.length <= 24).slice(0, 3).map((tag, i) => (
              <Badge key={i} variant="info-muted" size="xs" className="uppercase">
                {tag}
              </Badge>
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onOpenBlender}
              fullWidth
            >
              ✏️ Edit Architecture
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBrowserOpen(true)}
              className="flex-1 flex items-center justify-between bg-slate-950 border-2 border-slate-800 rounded-xl p-3 text-left hover:border-accent/50 transition-all group"
            >
              <span className={`text-sm font-black uppercase tracking-tight ${activePack ? 'text-accent' : 'text-slate-400'}`}>
                {activePack ? activePack.name.replace('VocalForge: ', '') : 'Browse Vocal Identities...'}
              </span>
              <ChevronRight size={16} className="text-slate-400 group-hover:text-accent transition-colors" />
            </button>
            <IconButton
              type="button"
              variant="ghost"
              size="xs"
              onClick={onOpenBlender}
              title="Open Blender"
              aria-label="Open Blender"
              className="text-slate-400 hover:text-accent"
            >
              🔀
            </IconButton>
          </div>

          <VocalProfileBrowser
            isOpen={browserOpen}
            onClose={() => setBrowserOpen(false)}
            onSelect={(key) => setSinglePack(key)}
            selectedKey={config.singlePackKey}
            contextLabel="Select Vocal Identity"
          />

          {activePack && (
            <div className="pl-4 border-l-2 border-slate-800 space-y-4 animate-in fade-in slide-in-from-left-2 duration-300">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Performance Role</label>
                <Select
                  value={config.voiceSpec}
                  onChange={(e) => setVoiceSpec(e.target.value as 'lead' | 'altA' | 'altB')}
                  options={voiceSpecOptions}
                  size="sm"
                  className="text-slate-200"
                />
                <p className="text-[11px] text-slate-400 italic">
                  {config.voiceSpec === 'lead' ? activePack.voiceLead :
                   config.voiceSpec === 'altA' ? activePack.voiceAltA : activePack.voiceAltB}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <Checkbox
                  checked={config.includeHarmonyStack}
                  onChange={(e) => setHarmonyStack(e.target.checked)}
                  label="Include Harmony Stack"
                />
                <Checkbox
                  checked={config.includeChoirGang}
                  onChange={(e) => setChoirGang(e.target.checked)}
                  label="Include Choir/Gang Vocals"
                />
              </div>

              <div className="space-y-2 pt-2 border-t border-slate-900">
                <div className="flex items-start gap-2">
                   <span className="text-[11px] font-bold text-slate-400 uppercase w-16">Styles</span>
                   <p className="text-[11px] text-slate-400">{activePack.styles.split(',').slice(0, 4).join(', ')}...</p>
                </div>
                <div className="flex items-start gap-2">
                   <span className="text-[11px] font-bold text-slate-400 uppercase w-16">Physics</span>
                   <p className="text-[11px] text-slate-400 line-clamp-2">{activePack.physics}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .vocal-pack-section { text-align: left; }
      `}</style>
    </div>
  );
}
